/**
 * Дефолты: тактика на капт, нарисованная поверх карты.
 *
 * Устроено как рабочий стол: слева доска с рядом инструментов над ней, справа
 * одна колонка — карта, объекты, дефолты и состав. Колонка одна, потому что
 * карты каптов квадратные: вторая колонка сбоку отнимала у карты столько
 * ширины, что она переставала помещаться в экран.
 *
 * Нарисованное живёт в состоянии страницы и в базу уходит только по кнопке.
 * Автосохранение здесь было бы вредно: доску открывают и просто посмотреть
 * чужой дефолт, а случайная линия переписала бы его для всех.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  TACTIC_COLORS,
  TACTIC_LIMITS,
  TACTIC_SIDE_LABEL,
  TACTIC_WIDTHS,
  type EventStatus,
  type TacticPreset,
  type TacticShape,
  type TacticSide,
  type Tier,
} from '@ak/shared';

import { apiDelete, apiPatch, apiPost } from '../lib/api.ts';
import { formatDateTime, formatRelative } from '../lib/format.ts';
import { CAR_IMAGES } from '../lib/car-images.ts';
import { callersFirst, drawShapes, describeShape, exportFileName, filterByNick } from '../lib/tactics.ts';
import { useResource } from '../lib/useResource.ts';
import { TacticBoard, type TacticTool } from '../components/tactic-board.tsx';
import { useToast } from '../components/toast.tsx';
import {
  IconArrow,
  IconBrush,
  IconCar,
  IconDashed,
  IconCaller,
  IconCollapse,
  IconCrewOff,
  IconDownload,
  IconEraser,
  IconExpand,
  IconEye,
  IconHand,
  IconEyeOff,
  IconMarker,
  IconRedo,
  IconTarget,
  IconTrash,
  IconUndo,
} from '../components/icons.tsx';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Panel,
  SegmentedControl,
  Select,
  Skeleton,
  TextInput,
  TierBadge,
} from '../components/ui.tsx';

interface MapsResponse {
  maps: { id: string; name: string; width: number; height: number; presets: number }[];
}

/** Составы каптов: из них набирают экипажи машин. */
interface RostersResponse {
  rosters: {
    id: number;
    status: EventStatus;
    startsAt: string | null;
    capacity: number;
    main: { userId: string; gameNick: string; tier: Tier; caller: boolean }[];
  }[];
}

interface PresetsResponse {
  presets: TacticPreset[];
  /** Имена тех, кто правил: в базе лежат только идентификаторы Discord. */
  names: Record<string, string>;
}

const TOOLS: { value: TacticTool; label: string; icon: ReactNode; hint: string }[] = [
  { value: 'hand', label: 'Рука', icon: <IconHand />, hint: 'Двигать нарисованное, а по пустому месту — саму карту' },
  { value: 'move', label: 'Заход', icon: <IconArrow />, hint: 'Куда идти: стрелка шагами' },
  { value: 'fire', label: 'Огонь', icon: <IconTarget />, hint: 'Куда стрелять: тонкая линия с прицелом на конце' },
  { value: 'brush', label: 'Маркер', icon: <IconBrush />, hint: 'Линия от руки: обвести дом, показать зону' },
  { value: 'dashed', label: 'Пунктир', icon: <IconDashed />, hint: 'Линия от руки пунктиром: запасной путь, вариант' },
  { value: 'marker', label: 'Метка', icon: <IconMarker />, hint: 'Кружок с номером: кто где стоит' },
  { value: 'car', label: 'Машина', icon: <IconCar />, hint: 'Ставит машину с выбранным экипажем — до четырёх человек' },
  { value: 'eraser', label: 'Ластик', icon: <IconEraser />, hint: 'Стирает фигуру целиком по нажатию на неё' },
];

const WIDTH_LABELS = ['Тонко', 'Средне', 'Толсто'];

const THIN = TACTIC_WIDTHS[0] ?? 0.002;
const MEDIUM = TACTIC_WIDTHS[1] ?? 0.004;

/**
 * Толщина по умолчанию у каждого инструмента.
 *
 * Маркер тонкий: им обводят дом и подчёркивают, а не рисуют магистрали.
 * Стрелки — средние: их должно быть видно через всю карту.
 */
const DEFAULT_WIDTH: Record<TacticTool, number> = {
  hand: MEDIUM,
  move: MEDIUM,
  fire: MEDIUM,
  brush: THIN,
  dashed: THIN,
  marker: MEDIUM,
  car: MEDIUM,
  eraser: MEDIUM,
};

/** Сколько шагов помнит отмена. Больше некуда: дефолт рисуется десятком фигур. */
const HISTORY_DEPTH = 50;

/**
 * Доска вместе с историей.
 *
 * Одним состоянием, а не тремя: отмена меняет и рисунок, и обе стопки разом,
 * и разложенные по разным `useState` они разъезжались бы при быстрых нажатиях.
 */
interface BoardState {
  shapes: TacticShape[];
  past: TacticShape[][];
  future: TacticShape[][];
}

const EMPTY_BOARD: BoardState = { shapes: [], past: [], future: [] };

export function TacticsPage({ timezone }: { timezone: string }): ReactNode {
  const toast = useToast();
  const maps = useResource<MapsResponse>('/api/tactics/maps');
  const [mapId, setMapId] = useState<string | null>(null);
  const [wide, setWide] = useState(false);

  /*
   * На этой вкладке страница не прокручивается.
   *
   * Колесо здесь занято картой и номерами меток, и заодно уезжавшая вниз
   * страница сбивала прицел. Всё, что не помещается, прокручивается внутри
   * своей колонки, поэтому недостижимого не остаётся.
   */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Карта по умолчанию — первая: пустая доска без выбранной карты выглядит
  // сломанной, а выбирать одно и то же каждый раз незачем.
  useEffect(() => {
    if (mapId === null && maps.data !== null && maps.data.maps.length > 0) {
      setMapId(maps.data.maps[0]?.id ?? null);
    }
  }, [mapId, maps.data]);

  const map = maps.data?.maps.find((item) => item.id === mapId) ?? null;

  if (maps.error !== null) {
    return <ErrorState message={maps.error} onRetry={maps.reload} />;
  }

  /*
   * Высота рабочей области считается от окна: полоса сервера сверху и поля
   * страницы снизу. Прокрутки на вкладке нет, поэтому вылезти за окно нельзя —
   * что не помещается, прокручивается внутри своей колонки.
   */
  return (
    <div className="flex flex-col gap-3" style={{ height: 'calc(100vh - 7rem)' }}>
      {maps.loading || maps.data === null ? (
        <Panel className="p-4">
          <Skeleton rows={6} />
        </Panel>
      ) : maps.data.maps.length === 0 ? (
        <Panel className="p-4">
          <EmptyState
            title="Карт пока нет"
            description="Положите картинки карт в server/assets/tactic-maps — по файлу на карту. Имя файла станет подписью."
          />
        </Panel>
      ) : (
        map !== null && (
          <MapWorkspace
            key={map.id}
            map={map}
            maps={maps.data.maps}
            timezone={timezone}
            wide={wide}
            onPickMap={setMapId}
            onWide={setWide}
            onSaved={() => {
              maps.reload();
            }}
            onError={(message) => {
              toast.error(message);
            }}
            onSuccess={(message) => {
              toast.success(message);
            }}
          />
        )
      )}
    </div>
  );
}

/**
 * Доска одной карты со своим набором дефолтов.
 *
 * Вынесено отдельным компонентом ради `key={map.id}`: при смене карты
 * состояние сбрасывается само, и на новой карте не остаётся стрелок с
 * прошлой.
 */
function MapWorkspace({
  map,
  maps,
  timezone,
  wide,
  onPickMap,
  onWide,
  onSaved,
  onError,
  onSuccess,
}: {
  map: { id: string; name: string; width: number; height: number };
  maps: { id: string; name: string; presets: number }[];
  timezone: string;
  wide: boolean;
  onPickMap: (id: string) => void;
  onWide: (wide: boolean) => void;
  onSaved: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}): ReactNode {
  const presets = useResource<PresetsResponse>(`/api/tactics/presets?mapId=${encodeURIComponent(map.id)}`);
  const rosters = useResource<RostersResponse>('/api/tactics/rosters');

  /*
   * Кого сажают в следующую машину.
   *
   * Выбор живёт рядом с доской, а не внутри машины: сначала отмечают четверых
   * в составе, потом ставят машину на карту. Так за один заход расставляется
   * весь выезд, а не по одному человеку на машину.
   */
  const [eventId, setEventId] = useState<number | null>(null);
  const [crew, setCrew] = useState<string[]>([]);

  const [board, setBoard] = useState<BoardState>(EMPTY_BOARD);
  const [tool, setTool] = useState<TacticTool>('move');
  const [color, setColor] = useState<string>(TACTIC_COLORS[0] ?? '#ff4d4d');
  const [widthByTool, setWidthByTool] = useState<Partial<Record<TacticTool, number>>>({});
  const [hidden, setHidden] = useState(false);
  // Плашки с никами скрываются отдельно от всей разметки: чаще всего нужна
  // карта со стрелками, но без четырёх ников у каждой машины.
  const [hideCrew, setHideCrew] = useState(false);

  const [side, setSide] = useState<TacticSide>('defence');
  const [openId, setOpenId] = useState<number | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  /** Какая машина сейчас правится: её место в списке фигур. */
  const [editingCar, setEditingCar] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * Развёрнутая доска.
   *
   * Состояние живёт на странице, а не здесь: в развороте прячется и шапка,
   * а она рисуется уровнем выше. Полный экран через `requestFullscreen` даёт
   * браузер и только в ответ на настоящее нажатие — его может не быть в
   * киоске, во встроенном окне или при запрете политикой. Разворот на
   * странице работает всегда, а полный экран добавляется сверху, если
   * браузер его дал.
   */
  const [full, setFull] = useState(false);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const width = widthByTool[tool] ?? DEFAULT_WIDTH[tool];

  /*
   * Последнее склеиваемое действие: чем оно было и когда.
   *
   * Подряд идущие правки с одним ключом — это одно действие, растянутое во
   * времени: прокрутка колеса на метке. Они складываются в один шаг отмены,
   * иначе Ctrl+Z откатывал бы номер метки по единице.
   */
  const merging = useRef<{ key: string; at: number } | null>(null);

  /** Правка разметки с запоминанием шага для отмены. */
  const apply = useCallback((next: TacticShape[], mergeKey?: string) => {
    const now = Date.now();
    const previous = merging.current;
    const continues =
      mergeKey !== undefined && previous !== null && previous.key === mergeKey && now - previous.at < 1000;

    merging.current = mergeKey === undefined ? null : { key: mergeKey, at: now };

    setBoard((current) => ({
      shapes: next,
      // Продолжение того же действия прошлый шаг не добавляет: он уже там.
      past: continues ? current.past : [...current.past, current.shapes].slice(-HISTORY_DEPTH),
      future: [],
    }));
  }, []);

  const undo = useCallback(() => {
    // Отмена обрывает склейку: следующая прокрутка — уже новое действие.
    merging.current = null;

    setBoard((current) => {
      const previous = current.past.at(-1);
      if (previous === undefined) {
        return current;
      }

      return { shapes: previous, past: current.past.slice(0, -1), future: [...current.future, current.shapes] };
    });
  }, []);

  const redo = useCallback(() => {
    merging.current = null;

    setBoard((current) => {
      const next = current.future.at(-1);
      if (next === undefined) {
        return current;
      }

      return { shapes: next, past: [...current.past, current.shapes], future: current.future.slice(0, -1) };
    });
  }, []);

  /*
   * Отмена с клавиатуры.
   *
   * Клавиша определяется по `code`, а не по `key`: в русской раскладке та же
   * клавиша присылает «я», и проверка на «z» не срабатывала — Ctrl+Z молчал
   * ровно у тех, кто эту тактику и рисует.
   */
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      const target = event.target as HTMLElement | null;
      if (target !== null && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }

      if (!event.ctrlKey && !event.metaKey) {
        return;
      }

      if (event.code === 'KeyZ') {
        event.preventDefault();
        if (event.shiftKey) {
          redo();
        } else {
          undo();
        }
      }

      // Ctrl+Y — привычный повтор в Windows, наравне с Ctrl+Shift+Z.
      if (event.code === 'KeyY') {
        event.preventDefault();
        redo();
      }
    }

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [redo, undo]);

  /*
   * Полный экран отдаёт доске всю высоту монитора.
   *
   * Состояние читается у браузера, а не хранится своё: выйти можно клавишей
   * Escape, и своё представление о том, развёрнуты мы или нет, разошлось бы
   * с настоящим.
   */
  useEffect(() => {
    function onChange(): void {
      const active = document.fullscreenElement !== null;
      setFull(active);

      // Escape выходит из полного экрана мимо нашей кнопки: разворот страницы
      // должен свернуться вместе с ним, иначе доска останется без панелей.
      if (!active) {
        onWide(false);
      }
    }

    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);

  const roster = rosters.data?.rosters.find((item) => item.id === eventId) ?? rosters.data?.rosters[0] ?? null;

  /*
   * Открытые наборы отделены от прошедших: доску открывают ради идущего
   * набора, и он должен быть виден сразу, а не угадываться по дате среди
   * десятка закрытых. Сервер отдаёт их первыми, поэтому без выбора вручную
   * подставится именно активный.
   */
  const activeRosters = (rosters.data?.rosters ?? []).filter((item) => item.status === 'open');
  const pastRosters = (rosters.data?.rosters ?? []).filter((item) => item.status !== 'open');

  const rosterOption = (item: RostersResponse['rosters'][number]): ReactNode => (
    <option key={item.id} value={String(item.id)}>
      {`${item.startsAt === null ? `набор ${String(item.id)}` : formatDateTime(item.startsAt, timezone)} · ${String(
        item.main.length,
      )}/${String(item.capacity)}`}
    </option>
  );

  /*
   * Кто уже сидит в машинах на доске.
   *
   * Такие люди уходят из списка совсем, а не просто гаснут: человек едет в
   * одной машине, и увидев его в списке второй раз, его туда и посадят.
   * Считается по доске, поэтому стёртая машина возвращает людей сама.
   */
  const seated = useMemo(
    () => new Set(board.shapes.flatMap((shape) => (shape.kind === 'car' ? shape.crew : []))),
    [board.shapes],
  );

  const [search, setSearch] = useState('');

  /*
   * Кого показывать в панели состава: свободных, а из них — подходящих под
   * поиск. Уже отмеченные остаются видны при любом запросе, иначе снять
   * отметку можно было бы только стерев его.
   */
  const available = useMemo(
    () =>
      callersFirst(filterByNick((roster?.main ?? []).filter((member) => !seated.has(member.gameNick)), search, crew)),
    [crew, roster, search, seated],
  );

  const open = presets.data?.presets.find((preset) => preset.id === openId) ?? null;
  const shown = useMemo(
    () => (presets.data?.presets ?? []).filter((preset) => preset.side === side),
    [presets.data, side],
  );

  function loadPreset(preset: TacticPreset): void {
    setBoard({ shapes: preset.shapes, past: [], future: [] });
    setOpenId(preset.id);
    setSide(preset.side);
  }

  async function saveNew(name: string): Promise<void> {
    setBusy(true);
    try {
      const result = await apiPost<{ preset: TacticPreset }>('/api/tactics/presets', {
        mapId: map.id,
        name,
        side,
        shapes: board.shapes,
      });

      setOpenId(result.preset.id);
      presets.reload();
      onSaved();
      onSuccess(`Дефолт «${result.preset.name}» сохранён`);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Не удалось сохранить дефолт');
    } finally {
      setBusy(false);
      setSaveOpen(false);
    }
  }

  async function overwrite(): Promise<void> {
    if (open === null) {
      return;
    }

    setBusy(true);
    try {
      await apiPatch(`/api/tactics/presets/${String(open.id)}`, { shapes: board.shapes, side });
      presets.reload();
      onSuccess(`Дефолт «${open.name}» перезаписан`);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Не удалось перезаписать дефолт');
    } finally {
      setBusy(false);
    }
  }

  async function remove(preset: TacticPreset): Promise<void> {
    if (!window.confirm(`Удалить дефолт «${preset.name}»? Это не отменяется.`)) {
      return;
    }

    setBusy(true);
    try {
      await apiDelete(`/api/tactics/presets/${String(preset.id)}`);
      if (openId === preset.id) {
        setOpenId(null);
      }

      presets.reload();
      onSaved();
      onSuccess(`Дефолт «${preset.name}» удалён`);
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Не удалось удалить дефолт');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Выгрузка в PNG.
   *
   * Картинка карты и разметка сводятся на холст в исходном размере карты:
   * снимок экрана отдал бы то, что влезло в панель, а тактику отправляют в
   * Discord, где её будут разглядывать.
   */
  async function download(): Promise<void> {
    const image = new Image();
    image.src = `/api/tactics/maps/${encodeURIComponent(map.id)}/image`;

    /*
     * Картинки машин грузятся отдельно и необязательны: не подгрузились — на
     * выгрузке будут нарисованные вектором машины, а не сорванная выгрузка.
     */
    const cars = await Promise.all(
      CAR_IMAGES.map(async (url) => {
        const picture = new Image();
        picture.src = url;

        return picture
          .decode()
          .then(() => picture)
          .catch(() => null);
      }),
    );

    try {
      await image.decode();

      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || map.width;
      canvas.height = image.naturalHeight || map.height;

      const context = canvas.getContext('2d');
      if (context === null) {
        throw new Error('Браузер не дал холст для выгрузки');
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      // Скрытые экипажи скрыты и в скачанной картинке: кнопку жмут как раз
      // перед тем, как отправить её в Discord.
      drawShapes(context, board.shapes, { width: canvas.width, height: canvas.height }, cars, hideCrew);

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = exportFileName(map.name, open?.name ?? null);
      link.click();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Не удалось собрать картинку');
    }
  }

  function toggleWide(): void {
    if (wide) {
      onWide(false);
      if (document.fullscreenElement !== null) {
        void document.exitFullscreen();
      }

      return;
    }

    onWide(true);
    // Отказ браузера не беда: доска уже развёрнута на странице, полный экран
    // добавляет только полосу под адресной строкой.
    void stageRef.current?.requestFullscreen().catch(() => undefined);
  }

  const ratio = map.height > 0 ? map.width / map.height : 16 / 9;

  return (
    /*
     * Три колонки под общей полосой инструментов.
     *
     * Инструменты стоят над картой, а слева от неё — список нарисованного:
     * его открывают чаще всего сразу после того, как поставили лишнее, и
     * искать его прокруткой в правой колонке было неудобно. Полоса
     * инструментов забирает у карты около полусотни пикселей высоты — это
     * плата за то, что и список, и состав видны без прокрутки.
     */
    <div
      ref={stageRef}
      className="flex min-h-0 flex-1 gap-3"
      style={full ? { background: 'var(--surface-page)', padding: '0.75rem', height: '100vh' } : undefined}
    >
      <Panel className="flex min-h-0 flex-1 flex-col gap-2 p-3">
        {/*
         * Инструменты — внутри панели карты, а не отдельной полосой над ней:
         * отдельная полоса стоила бы карте ещё поля панели и промежуток, а
         * высота здесь дороже всего.
         */}
        <div className="flex items-center gap-1.5">
          <ToolBar
            tool={tool}
            color={color}
            width={width}
            hidden={hidden}
            hideCrew={hideCrew}
            wide={wide}
            canUndo={board.past.length > 0}
            canRedo={board.future.length > 0}
            canClear={board.shapes.length > 0}
            onTool={setTool}
            onColor={setColor}
            onWidth={(value) => {
              setWidthByTool((current) => ({ ...current, [tool]: value }));
            }}
            onToggleHidden={() => {
              setHidden((value) => !value);
            }}
            onToggleCrew={() => {
              setHideCrew((value) => !value);
            }}
            onUndo={undo}
            onRedo={redo}
            onClear={() => {
              apply([]);
            }}
            onDownload={() => {
              void download();
            }}
            onToggleWide={toggleWide}
          />
        </div>

        <TacticBoard
          imageUrl={`/api/tactics/maps/${encodeURIComponent(map.id)}/image`}
          mapName={map.name}
          ratio={ratio}
          shapes={board.shapes}
          tool={tool}
          color={color}
          width={width}
          hidden={hidden}
          hideCrew={hideCrew}
          crew={crew}
          onEditCar={setEditingCar}
          /*
           * Запас — всё, что на экране занято не доской: полоса сервера
           * сверху, поля страницы, ряд инструментов и поля панели. Подобран
           * замером на живой админке.
           */
          reserve={full ? '5rem' : '11.5rem'}
          onChange={(next, mergeKey) => {
            // Ключ склейки передаётся дальше как есть: без него прокрутка
            // колеса на метке разошлась бы на шаг отмены за щелчок.
            apply(next, mergeKey);

            // Экипаж высаживается из панели, как только машина встала:
            // следующую четвёрку набирают заново, а не разбирают старую.
            if (tool === 'car' && next.length > board.shapes.length) {
              setCrew([]);
            }
          }}
        />
      </Panel>

      {/*
       * Одна колонка сбоку, а не две: при окне 1462 px две колонки по бокам
       * оставляли карте 568 px вместо 830. «Объекты» стоят первыми — их
       * открывают сразу после того, как поставили лишнее, и докручивать до
       * них не нужно.
       */}
      <div className={wide ? 'hidden' : 'flex min-h-0 w-[19rem] shrink-0 flex-col gap-3'}>
        {/*
         * Выбор карты — здесь, а не в полосе инструментов: там он переносил
         * её на две строки и забирал у карты полсотни пикселей высоты.
         */}
        <Panel className="flex shrink-0 items-center gap-2 p-2">
          <span className="shrink-0 pl-1 text-sm font-medium">Карта</span>
          <div className="min-w-0 flex-1">
            <Select
              value={map.id}
              aria-label="Карта"
              onChange={(event) => {
                onPickMap(event.target.value);
              }}
            >
              {maps.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.presets > 0 ? `${item.name} · ${String(item.presets)}` : item.name}
                </option>
              ))}
            </Select>
          </div>
        </Panel>

        <Panel className="flex max-h-56 min-h-0 shrink-0 flex-col p-3">
          <div className="mb-2 flex shrink-0 items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">Объекты</h2>
            <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
              {String(board.shapes.length)}
            </span>
          </div>

          <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto text-xs">
            {board.shapes.map((shape, index) => (
              <li key={index} className="flex items-center justify-between gap-1.5">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="size-2.5 shrink-0 rounded-full" style={{ background: shape.color }} />
                  <span className="truncate" title={describeShape(shape)}>
                    {describeShape(shape)}
                  </span>
                </span>
                <button
                  type="button"
                  className="shrink-0 cursor-pointer"
                  style={{ color: 'var(--text-muted)' }}
                  title="Убрать"
                  onClick={() => {
                    apply(board.shapes.filter((_, position) => position !== index));
                  }}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel className="flex shrink-0 flex-col gap-2 p-3">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">Дефолты</h2>
            {presets.refreshing && (
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                обновляется…
              </span>
            )}
          </div>

          <SegmentedControl<TacticSide>
            label="Сторона"
            value={side}
            onChange={setSide}
            options={[
              { value: 'defence', label: TACTIC_SIDE_LABEL.defence, color: 'var(--tier-3)' },
              { value: 'attack', label: TACTIC_SIDE_LABEL.attack, color: 'var(--tier-1)' },
            ]}
          />

          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              disabled={busy || board.shapes.length === 0}
              onClick={() => {
                setSaveOpen(true);
              }}
            >
              Сохранить как…
            </Button>
            {open !== null && (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  void overwrite();
                }}
              >
                Перезаписать
              </Button>
            )}
            {(board.shapes.length > 0 || open !== null) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setBoard(EMPTY_BOARD);
                  setOpenId(null);
                }}
              >
                Новый лист
              </Button>
            )}
          </div>

          {shown.length > 0 && (
            <ul className="max-h-40 space-y-1.5 overflow-y-auto">
              {shown.map((preset) => (
                <li
                  key={preset.id}
                  className="rounded-lg border px-2 py-1.5"
                  style={{
                    borderColor: preset.id === openId ? 'var(--accent)' : 'var(--border-subtle)',
                    background: 'var(--surface-raised)',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      className="min-w-0 cursor-pointer truncate text-left text-sm font-medium"
                      title={formatDateTime(preset.updatedAt, timezone)}
                      onClick={() => {
                        loadPreset(preset);
                      }}
                    >
                      {preset.name}
                    </button>
                    <button
                      type="button"
                      className="shrink-0 cursor-pointer text-xs"
                      style={{ color: 'var(--text-muted)' }}
                      title="Удалить дефолт"
                      disabled={busy}
                      onClick={() => {
                        void remove(preset);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {String(preset.shapes.length)} фигур · {formatRelative(preset.updatedAt)}
                    {preset.updatedBy !== null && ` · ${presets.data?.names[preset.updatedBy] ?? preset.updatedBy}`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="flex min-h-0 flex-1 flex-col gap-2 p-3">
          <div className="flex shrink-0 items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">Состав</h2>
            <span className="text-xs tabular-nums" style={{ color: 'var(--text-muted)' }}>
              {String(crew.length)} / {String(TACTIC_LIMITS.crew)}
              {seated.size > 0 && ` · едут ${String(seated.size)}`}
            </span>
          </div>

          {rosters.loading ? (
            <Skeleton rows={3} />
          ) : (
            <>
              <Select
                value={String(roster?.id ?? '')}
                aria-label="Набор"
                onChange={(event) => {
                  setEventId(Number(event.target.value));
                  setCrew([]);
                  setSearch('');
                }}
              >
                {activeRosters.length > 0 && <optgroup label="Активные наборы">{activeRosters.map(rosterOption)}</optgroup>}
                {pastRosters.length > 0 && <optgroup label="Прошедшие">{pastRosters.map(rosterOption)}</optgroup>}
              </Select>

              {/*
                Без этой строки пустота объясняется неверно: человек видит одни
                прошедшие наборы и решает, что доска их и показывает. Набор
                открыт лишь те полчаса, что идёт запись, — дальше он
                закрывается сам, когда капт начался.
              */}
              {activeRosters.length === 0 && (
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Открытого набора на капт сейчас нет — показаны прошедшие.
                </span>
              )}

              {crew.length > 0 && (
                <div className="flex shrink-0 flex-wrap gap-1">
                  {crew.map((name) => (
                    <Badge key={name} tone="accent">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}

              <TextInput
                placeholder="Поиск по нику"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                }}
              />

              <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">
                {available.map((member) => {
                  const picked = crew.includes(member.gameNick);
                  const packed = crew.length >= TACTIC_LIMITS.crew;

                  return (
                    <li key={member.userId}>
                      <button
                        type="button"
                        disabled={!picked && packed}
                        aria-pressed={picked}
                        className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-2 py-1 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-45"
                        style={{
                          borderColor: picked ? 'color-mix(in oklab, var(--accent) 45%, transparent)' : 'transparent',
                          background: picked ? 'var(--accent-soft)' : 'transparent',
                          color: picked ? 'var(--accent)' : 'var(--text-primary)',
                        }}
                        onClick={() => {
                          setCrew((current) =>
                            current.includes(member.gameNick)
                              ? current.filter((name) => name !== member.gameNick)
                              : [...current, member.gameNick].slice(0, TACTIC_LIMITS.crew),
                          );
                        }}
                      >
                        <span className="flex min-w-0 items-center gap-1.5">
                          {member.caller && <CallerMark />}
                          <span className="truncate">{member.gameNick}</span>
                        </span>
                        <TierBadge tier={member.tier} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Panel>
      </div>

      {editingCar !== null &&
        (() => {
          const target = board.shapes[editingCar];
          if (target === undefined || target.kind !== 'car') {
            return null;
          }

          // В списке доступны все из состава, кроме сидящих в других машинах:
          // свой экипаж должен остаться выбираемым, иначе его не переставить.
          const busyNames = new Set(
            board.shapes.flatMap((shape, index) =>
              shape.kind === 'car' && index !== editingCar ? shape.crew : [],
            ),
          );

          return (
            <CrewModal
              crew={target.crew}
              people={(roster?.main ?? []).filter((member) => !busyNames.has(member.gameNick))}
              onClose={() => {
                setEditingCar(null);
              }}
              onSave={(next) => {
                apply(
                  board.shapes.map((shape, index) =>
                    index === editingCar && shape.kind === 'car' ? { ...shape, crew: next } : shape,
                  ),
                );
                setEditingCar(null);
              }}
              onDelete={() => {
                // Экипаж освобождать отдельно не нужно: занятость считается по
                // машинам на доске, и снятая машина никого больше не держит.
                apply(board.shapes.filter((_, index) => index !== editingCar));
                setEditingCar(null);
              }}
            />
          );
        })()}

      {saveOpen && (
        <SavePresetModal
          side={side}
          busy={busy}
          onClose={() => {
            setSaveOpen(false);
          }}
          onSave={(name) => {
            void saveNew(name);
          }}
        />
      )}
    </div>
  );
}

/**
 * Полоса инструментов над картой.
 *
 * Горизонтальная, чтобы слева от карты помещался список нарисованного: его
 * открывают чаще, чем меняют цвет, и прокручивать к нему правую колонку было
 * неудобно. Подписи — в подсказках: восемь кнопок с подписями заняли бы всю
 * ширину.
 */
function ToolBar({
  tool,
  color,
  width,
  hidden,
  hideCrew,
  wide,
  canUndo,
  canRedo,
  canClear,
  onTool,
  onColor,
  onWidth,
  onToggleHidden,
  onToggleCrew,
  onUndo,
  onRedo,
  onClear,
  onDownload,
  onToggleWide,
}: {
  tool: TacticTool;
  color: string;
  width: number;
  hidden: boolean;
  hideCrew: boolean;
  wide: boolean;
  canUndo: boolean;
  canRedo: boolean;
  canClear: boolean;
  onTool: (tool: TacticTool) => void;
  onColor: (color: string) => void;
  onWidth: (width: number) => void;
  onToggleHidden: () => void;
  onToggleCrew: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onClear: () => void;
  onDownload: () => void;
  onToggleWide: () => void;
}): ReactNode {
  const [openPanel, setOpenPanel] = useState<'color' | 'width' | null>(null);

  return (
    <>
      {TOOLS.map((item) => (
        <BarButton
          key={item.value}
          label={item.label}
          hint={`${item.label} — ${item.hint}`}
          active={tool === item.value}
          onClick={() => {
            onTool(item.value);
          }}
        >
          {item.icon}
        </BarButton>
      ))}

      <Divider />

      {/*
       * Цвет и толщина спрятаны под кнопки и раскрываются панелью снизу:
       * восемь кружков и три точки в ряд разгоняли полосу на всю ширину, а
       * открытыми они нужны на секунду.
       */}
      <BarPopover
        label="Цвет"
        hint="Цвет разметки"
        open={openPanel === 'color'}
        onToggle={() => {
          setOpenPanel((current) => (current === 'color' ? null : 'color'));
        }}
        onClose={() => {
          setOpenPanel(null);
        }}
        badge={
          <span className="size-4 rounded-full border" style={{ background: color, borderColor: 'var(--border-strong)' }} />
        }
      >
        <div className="grid grid-cols-4 gap-2">
          {TACTIC_COLORS.map((item) => (
            <button
              key={item}
              type="button"
              aria-label={`Цвет ${item}`}
              aria-pressed={color === item}
              className="size-7 cursor-pointer rounded-full border-2 transition-transform hover:scale-110"
              style={{ background: item, borderColor: color === item ? 'var(--text-primary)' : 'var(--border-subtle)' }}
              onClick={() => {
                onColor(item);
                setOpenPanel(null);
              }}
            />
          ))}
        </div>
      </BarPopover>

      <BarPopover
        label="Толщина"
        hint="Толщина линии"
        open={openPanel === 'width'}
        onToggle={() => {
          setOpenPanel((current) => (current === 'width' ? null : 'width'));
        }}
        onClose={() => {
          setOpenPanel(null);
        }}
        badge={
          <span
            className="rounded-full"
            style={{
              background: 'currentColor',
              width: `${String(3 + TACTIC_WIDTHS.indexOf(width) * 3)}px`,
              height: `${String(3 + TACTIC_WIDTHS.indexOf(width) * 3)}px`,
            }}
          />
        }
      >
        <div className="flex flex-col gap-1">
          {TACTIC_WIDTHS.map((item, index) => (
            <button
              key={item}
              type="button"
              aria-pressed={width === item}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm"
              style={{
                background: width === item ? 'var(--accent-soft)' : 'transparent',
                color: width === item ? 'var(--accent)' : 'var(--text-secondary)',
              }}
              onClick={() => {
                onWidth(item);
                setOpenPanel(null);
              }}
            >
              <span
                className="rounded-full"
                style={{
                  background: 'currentColor',
                  width: `${String(4 + index * 4)}px`,
                  height: `${String(4 + index * 4)}px`,
                }}
              />
              {WIDTH_LABELS[index]}
            </button>
          ))}
        </div>
      </BarPopover>

      <Divider />

      <BarButton label="Отменить" hint="Отменить (Ctrl+Z)" disabled={!canUndo} onClick={onUndo}>
        <IconUndo />
      </BarButton>
      <BarButton label="Повторить" hint="Повторить (Ctrl+Shift+Z)" disabled={!canRedo} onClick={onRedo}>
        <IconRedo />
      </BarButton>
      <BarButton
        label={hidden ? 'Показать разметку' : 'Скрыть разметку'}
        hint={hidden ? 'Показать разметку' : 'Посмотреть карту без разметки'}
        active={hidden}
        onClick={onToggleHidden}
      >
        {hidden ? <IconEyeOff /> : <IconEye />}
      </BarButton>
      <BarButton
        label={hideCrew ? 'Показать экипажи' : 'Скрыть экипажи'}
        hint={hideCrew ? 'Вернуть плашки с никами' : 'Убрать плашки с никами: останутся только машины'}
        active={hideCrew}
        onClick={onToggleCrew}
      >
        <IconCrewOff />
      </BarButton>
      <BarButton label="Скачать" hint="Скачать картинку с разметкой" onClick={onDownload}>
        <IconDownload />
      </BarButton>
      <BarButton label="Очистить" hint="Убрать всю разметку" disabled={!canClear} onClick={onClear}>
        <IconTrash />
      </BarButton>
      <BarButton
        label={wide ? 'Свернуть' : 'Во весь экран'}
        hint={wide ? 'Вернуть панели (Esc)' : 'Развернуть доску: панели уходят, карта занимает весь экран'}
        active={wide}
        onClick={onToggleWide}
      >
        {wide ? <IconCollapse /> : <IconExpand />}
      </BarButton>
    </>
  );
}

/** Кнопка полосы, которая раскрывает панель под собой. */
function BarPopover({
  label,
  hint,
  open,
  badge,
  onToggle,
  onClose,
  children,
}: {
  label: string;
  hint: string;
  open: boolean;
  badge: ReactNode;
  onToggle: () => void;
  onClose: () => void;
  children: ReactNode;
}): ReactNode {
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Панель закрывается нажатием мимо и клавишей Escape: открытая, она висит
  // поверх карты и мешает рисовать.
  useEffect(() => {
    if (!open) {
      return;
    }

    function onDocumentDown(event: MouseEvent): void {
      if (boxRef.current !== null && !boxRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('mousedown', onDocumentDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onDocumentDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div ref={boxRef} className="relative">
      <BarButton label={label} hint={hint} active={open} onClick={onToggle}>
        {badge}
      </BarButton>

      {open && (
        /*
         * `w-max` обязателен: панель лежит absolute внутри кнопки и без него
         * берёт её ширину за доступную — кружки сминались в столбик.
         */
        <div
          className="absolute top-11 left-0 z-20 w-max rounded-xl border p-2 shadow-lg"
          style={{ background: 'var(--surface-panel)', borderColor: 'var(--border-subtle)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function Divider(): ReactNode {
  return <span className="mx-1 h-6 w-px shrink-0" style={{ background: 'var(--border-subtle)' }} />;
}

function BarButton({
  label,
  hint,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  hint: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      title={hint}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      className="grid size-9 shrink-0 cursor-pointer place-items-center rounded-xl border transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-40"
      style={{
        color: active ? 'var(--accent)' : 'var(--text-secondary)',
        background: active ? 'var(--accent-soft)' : 'var(--surface-raised)',
        borderColor: active ? 'color-mix(in oklab, var(--accent) 45%, transparent)' : 'var(--border-subtle)',
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}


function CrewModal({
  crew,
  people,
  onClose,
  onSave,
  onDelete,
}: {
  crew: readonly string[];
  people: { userId: string; gameNick: string; tier: Tier; caller: boolean }[];
  onClose: () => void;
  onSave: (crew: string[]) => void;
  onDelete: () => void;
}): ReactNode {
  const [picked, setPicked] = useState<string[]>([...crew]);
  const [search, setSearch] = useState('');

  /*
   * Поиск по нику.
   *
   * В составе капта до тридцати пяти человек, и искать нужного глазами в
   * списке дольше, чем набрать три буквы. Уже посаженные из списка не
   * исчезают, даже если не подходят под поиск: иначе выбранного нельзя было бы
   * снять, не стерев запрос.
   */
  const found = useMemo(() => callersFirst(filterByNick(people, search, picked)), [people, picked, search]);

  return (
    <Modal
      title="Кто едет"
      onClose={onClose}
      footer={
        <>
          {/*
            Удаление отодвинуто в левый край: «Сохранить» нажимают не глядя, и
            соседняя кнопка рано или поздно ловит этот промах. Спрашивать
            подтверждение не стали — снятая машина возвращается общей отменой,
            как и всё остальное на доске.
          */}
          <Button variant="danger" className="mr-auto" onClick={onDelete}>
            Убрать машину
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={() => {
              onSave(picked);
            }}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="mb-2 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <TextInput
            autoFocus
            placeholder="Поиск по нику"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
        </div>
        <span className="shrink-0 text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>
          {String(picked.length)} / {String(TACTIC_LIMITS.crew)}
        </span>
      </div>

      {people.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Состав пуст: выбирать не из кого.
        </p>
      ) : found.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          Никого с таким ником в составе нет.
        </p>
      ) : (
        <ul className="max-h-80 space-y-1 overflow-y-auto">
          {found.map((member) => {
            const inCar = picked.includes(member.gameNick);
            const full = picked.length >= TACTIC_LIMITS.crew;

            return (
              <li key={member.userId}>
                <button
                  type="button"
                  disabled={!inCar && full}
                  aria-pressed={inCar}
                  className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-45"
                  style={{
                    borderColor: inCar ? 'color-mix(in oklab, var(--accent) 45%, transparent)' : 'transparent',
                    background: inCar ? 'var(--accent-soft)' : 'transparent',
                    color: inCar ? 'var(--accent)' : 'var(--text-primary)',
                  }}
                  onClick={() => {
                    setPicked((current) =>
                      current.includes(member.gameNick)
                        ? current.filter((name) => name !== member.gameNick)
                        : [...current, member.gameNick].slice(0, TACTIC_LIMITS.crew),
                    );
                  }}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    {member.caller && <CallerMark />}
                    <span className="truncate">{member.gameNick}</span>
                  </span>
                  <TierBadge tier={member.tier} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

function SavePresetModal({
  side,
  busy,
  onClose,
  onSave,
}: {
  side: TacticSide;
  busy: boolean;
  onClose: () => void;
  onSave: (name: string) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const trimmed = name.trim();

  return (
    <Modal
      title="Сохранить дефолт"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            disabled={busy || trimmed === ''}
            onClick={() => {
              onSave(trimmed);
            }}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <Field label="Название" hint={`Сохранится со стороной «${TACTIC_SIDE_LABEL[side]}»`}>
        <TextInput
          autoFocus
          maxLength={60}
          placeholder="Например: заход через гараж"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && trimmed !== '') {
              onSave(trimmed);
            }
          }}
        />
      </Field>
    </Modal>
  );
}

/**
 * Пометка коллера в списке состава.
 *
 * Рупор в кружке цвета акцента: коллеров в составе двое-трое, и глаз должен
 * находить их сразу, не читая. Подпись словом заняла бы половину строки, а
 * ники и так длинные.
 */
function CallerMark(): ReactNode {
  return (
    <span
      className="grid size-5 shrink-0 place-items-center rounded-full"
      style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
      title="Коллер: ведёт состав на капте"
      aria-label="Коллер"
    >
      <IconCaller size={13} />
    </span>
  );
}
